import { useParams, usePathname } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'

import { Size } from '@syconium/little-miss-figgy'

import { useCartDialog } from '../../app/_providers/CartProvider.client'
import { usePortal } from '../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'
import { CartContainer, UseCartNewCartItem } from '../../brunswick/containers/cart'
import { WaitlistService } from '../../components/pages/products/ProductPage/contexts/WaitlistService'
import { EmbroideryService } from '../../containers/EmbroideryService'
import { Variant } from '../../types/graphql'

export interface AddToBagErrors {
  missingRequiredSize: boolean
  missingRequiredLogoEmbroidery: boolean
  missingRequiredTextEmbroidery: boolean
}

interface Props {
  isPortalColorSelected?: boolean
  isProductFinalSale: boolean
  isProductWaitlistable: boolean
  requiresSizeSelection: boolean
  selectedVariant: Variant | null | undefined
  selectedSize: Size | undefined
  swPromoEligible: boolean | undefined
  isProductEmbroiderable?: boolean
}

export interface Output {
  addToBag: (properties?: Record<string, string>) => void
  addToBagErrors: AddToBagErrors
  handleAddToBagClick: () => Promise<void>
  isAddToCartInFlight: boolean
  quantity: number
  resetInvalidBagClickStatus: () => void
  setQuantity: (quantity: number) => void
}

export function useBag({
  isProductFinalSale,
  isProductWaitlistable,
  requiresSizeSelection,
  selectedSize,
  selectedVariant,
  swPromoEligible,
  isPortalColorSelected,
  isProductEmbroiderable,
}: Props): Output {
  const { addItems, cart } = CartContainer.useContainer()
  const { open: openCartDialog } = useCartDialog()
  const embroideryService = EmbroideryService.useContainer()
  const { openWaitlistModal } = WaitlistService.useContainer()
  const params = useParams()
  const pathname = usePathname()
  const {
    cart: { finalSale, nonReturnable },
  } = useTranslation()
  const { hasMandatoryTextEmbroidery, hasMandatoryLogoEmbroidery, portal } = usePortal()

  const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery(selectedVariant?.category ?? '')
  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery(selectedVariant?.category ?? '')

  // Check portal embroidery settings against the currently confirmed embroidery selection
  const { isMissingRequiredLogoEmbroidery, isMissingRequiredTextEmbroidery } = useMemo(() => {
    const { selectedEmbroideryFormOptions, confirmAddEmbroidery } = embroideryService

    // Determine if portal embroidery is defined as required
    const isTextEmbroideryRequired =
      isPortalColorSelected && isProductEmbroiderable && mandatoryTextEmbroidery

    const isLogoEmbroideryRequired =
      isPortalColorSelected && isProductEmbroiderable && mandatoryLogoEmbroidery

    // Determine if selected embroidery items are valid for what is defined by the portal
    const isMissingRequiredTextEmbroidery =
      isTextEmbroideryRequired &&
      (!selectedEmbroideryFormOptions?.textFirstLine ||
        (!confirmAddEmbroidery && selectedEmbroideryFormOptions.textFirstLine))

    const isMissingRequiredLogoEmbroidery =
      isLogoEmbroideryRequired &&
      (!selectedEmbroideryFormOptions?.iconImageSource ||
        (!confirmAddEmbroidery && selectedEmbroideryFormOptions.iconImageSource))

    return { isMissingRequiredTextEmbroidery, isMissingRequiredLogoEmbroidery }
  }, [
    isProductEmbroiderable,
    mandatoryLogoEmbroidery,
    mandatoryTextEmbroidery,
    isPortalColorSelected,
    embroideryService,
  ])

  // required for `_collectionAttribution` property
  const collectionHandle: string | undefined = useMemo(() => {
    if (pathname?.startsWith('/collections') && !(params?.handle instanceof Array)) {
      return params?.handle ?? undefined
    }
    return undefined
  }, [params, pathname])

  // This property is used as a visible (no underscore prefix) line item on the minicart and checkout page
  const finalSaleProperty = {
    [finalSale]: nonReturnable,
  }

  const [quantity, setQuantity] = useState<number>(1)

  const [addToBagErrors, setAddToBagErrors] = useState<AddToBagErrors>({
    missingRequiredSize: false,
    missingRequiredTextEmbroidery: false,
    missingRequiredLogoEmbroidery: false,
  })

  const [isAddToCartInFlight, setIsAddToCartInFlight] = useState<boolean>(false)

  async function handleAddToBagClick(): Promise<void> {
    // Collect validation errors when ATC is clicked
    let errors: Partial<AddToBagErrors> = {}

    const hasSizeErrors =
      ((requiresSizeSelection && !selectedSize) || selectedSize?.soldOut) && !isProductWaitlistable

    // If no size selected, scroll to size selector and set error
    if (hasSizeErrors) {
      errors.missingRequiredSize = true
      const sizeSectionTitle = document.getElementsByClassName(
        'size-section-title'
      )[0] as HTMLElement | null
      if (sizeSectionTitle) {
        try {
          sizeSectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' })
          sizeSectionTitle.focus({ preventScroll: true })
        } catch {}
      }
    }

    // If mandatory text embroidery is required for the user & no text embroidery is present
    if (isMissingRequiredTextEmbroidery) {
      errors.missingRequiredTextEmbroidery = true
    }

    // If mandatory logo embroidery is required for the user & no logo embroidery is present, manage error state
    if (isMissingRequiredLogoEmbroidery) {
      errors.missingRequiredLogoEmbroidery = true
    }

    // If any validation errors exist, update error state
    //
    // @TODO: This check should also take into consideration if there is a missing required logo embroidery,
    // but the current logic does not allow for us to add a logo through the modal without also adding text embroidery.
    // This needs to be revisited to properly handle the case where a portal only enforces mandatory text or logo embroidery individually.
    if (errors.missingRequiredSize || errors.missingRequiredTextEmbroidery) {
      setAddToBagErrors(prevErrors => ({
        ...prevErrors,
        ...errors,
      }))
    } else if (isProductWaitlistable && selectedSize && selectedSize.soldOut) {
      // if no errors & product is waitlistable, open waitlist modal
      openWaitlistModal()
    } else {
      // Otherwise, add the products to the cart

      // Portal-specific line item properties
      const portalAttributionProperties: Record<string, string> =
        isPortalColorSelected && portal?.name ? { _portalAttribution: portal.name } : {}

      // Collection attribution properties (for data team analytics)
      const collectionAttributionProperties: Record<string, string> = collectionHandle
        ? { _collectionAttribution: collectionHandle }
        : {}

      const properties = {
        ...portalAttributionProperties,
        ...collectionAttributionProperties,
      }

      const embroideryProperties = {
        ...portalAttributionProperties,
      }

      await addToBag(properties, embroideryProperties)
    }
  }

  const resetInvalidBagClickStatus = useCallback(() => {
    setAddToBagErrors({
      missingRequiredSize: false,
      missingRequiredLogoEmbroidery: false,
      missingRequiredTextEmbroidery: false,
    })
  }, [])

  /**
   * Performs checks around embroidery and line item properties (if present) before adding an item to the bag.
   *
   * @param properties line item properties that will be applied to the garment.
   * @param embroideryProperties line item properties that will be applied to the embroidery item, if applicable.
   */
  function addToBag(
    properties?: Record<string, string>,
    embroideryProperties?: Record<string, string>
  ): void {
    if (!selectedVariant) return

    const {
      selectedEmbroideryFormOptions,
      defaultSelectedEmbroideryOptions,
      confirmAddEmbroidery,
      embroideryLineItemProperties,
    } = embroideryService

    // Consider embroidery confirmed if the user has explicitly confirmed, or if default options were provided
    const isEmbroideryConfirmed =
      confirmAddEmbroidery || Object.keys(defaultSelectedEmbroideryOptions ?? []).length > 0

    const hasSelectedEmbroidery =
      Object.keys(selectedEmbroideryFormOptions).length > 0 &&
      isEmbroideryConfirmed &&
      selectedEmbroideryFormOptions?.embroideryItemVariant

    const variantGid: string = selectedVariant.shopifyId
    setIsAddToCartInFlight(true)

    if (hasSelectedEmbroidery && embroideryLineItemProperties) {
      const itemsToAdd: UseCartNewCartItem[] = []
      let logoEmbroiderySetupInCart = false
      for (let key in cart?.items) {
        if (
          cart?.items[key]?.properties?._logo_setup_embroideryId ===
          embroideryLineItemProperties._logo_setup_embroideryId
        ) {
          logoEmbroiderySetupInCart = true
        }
      }
      itemsToAdd.push(
        {
          item: selectedEmbroideryFormOptions.embroideryItemVariant!,
          properties: {
            ...embroideryProperties,
            ...embroideryLineItemProperties,
            ...finalSaleProperty,
            ...(!logoEmbroiderySetupInCart &&
              selectedEmbroideryFormOptions.embroiderySetupVariant && {
                _logo_setup_fee:
                  selectedEmbroideryFormOptions.embroiderySetupVariant.price.toString(),
              }),
          },
          quantity,
          swPromoEligible,
          isPortalLogo: isPortalColorSelected && mandatoryLogoEmbroidery,
        },
        {
          item: selectedVariant,
          properties: {
            ...finalSaleProperty,
            ...properties,
            _embroideryId: variantGid,
            _guid: embroideryLineItemProperties._guid,
            _LPROP: embroideryLineItemProperties._LPROP,
            _logo_id: embroideryLineItemProperties._logo_id,
          },
          quantity,
          swPromoEligible,
          isPortalColor: isPortalColorSelected,
        }
      )
      if (embroideryLineItemProperties._logo_setup_embroideryId) {
        if (!logoEmbroiderySetupInCart) {
          itemsToAdd.push({
            finalSale: isProductFinalSale,
            item: selectedEmbroideryFormOptions.embroiderySetupVariant!,
            properties: {
              ...finalSaleProperty,
              ...properties,
              _embroidery_version: embroideryLineItemProperties._embroidery_version,
              _scrub_color: embroideryLineItemProperties._scrub_color,
              _logo_id: embroideryLineItemProperties._logo_id,
              _guid: embroideryLineItemProperties._logo_setup_guid,
              _logo_preview: embroideryLineItemProperties._logo_preview,
              logo_name: embroideryLineItemProperties.logo_name,
              logo_placement: embroideryLineItemProperties.logo_placement,
              logo_color_option: embroideryLineItemProperties.logo_color_option,
              _is_logo_setup: 'true',
            },
            quantity,
            swPromoEligible,
          })
        }
      }
      addItems(itemsToAdd)
    } else {
      addItems([
        {
          finalSale: isProductFinalSale,
          item: selectedVariant,
          properties: {
            ...(isProductFinalSale ? finalSaleProperty : {}),
            ...(Boolean(properties) ? properties : {}),
          },
          quantity,
          swPromoEligible,
        },
      ])
    }

    setIsAddToCartInFlight(false)
    openCartDialog()
  }

  return {
    addToBag,
    addToBagErrors,
    handleAddToBagClick,
    isAddToCartInFlight,
    quantity,
    resetInvalidBagClickStatus,
    setQuantity,
  }
}
